import { useEffect, useRef } from 'react';
import { CircleAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type VariableOption = {
  label: string;
  value: string;
};

export type Chunk =
  | { type: 'text'; value: string; id: string }
  | { type: 'field'; value: string; label?: string; id: string };

interface VariableChunkEditorProps {
  chunks: Chunk[];
  onTextChange: (chunkId: string, value: string) => void;
  onFieldRemove: (fieldId: string) => void;
  onBackspaceOnEmpty: (chunkId: string) => void;
  onAddVariable: (variable: VariableOption) => void;
  variables: VariableOption[];
  variantLabel?: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  maxLength?: number;
  currentLength?: number;
}

export function VariableChunkEditor({
  chunks,
  onTextChange,
  onFieldRemove,
  onBackspaceOnEmpty,
  onAddVariable,
  variables,
  variantLabel,
  placeholder: placeholderProp,
  hint,
  error,
  maxLength,
  currentLength: currentLengthProp,
}: VariableChunkEditorProps) {
  const { t } = useTranslation('common');
  const placeholder = placeholderProp ?? t('variableEditor.placeholder');
  const currentLength = currentLengthProp ?? chunks.reduce((sum, c) => sum + c.value.length, 0);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const measureSpan = useRef<HTMLSpanElement | null>(null);
  const prevChunksRef = useRef<Chunk[]>(chunks);

  const adjustWidth = (input: HTMLInputElement, val: string, ph?: string) => {
    let span = measureSpan.current;
    if (!span) {
      span = document.createElement('span');
      span.style.cssText = 'visibility:hidden;position:absolute;top:0;left:-9999px;white-space:pre';
      document.body.appendChild(span);
      measureSpan.current = span;
    }
    const styles = window.getComputedStyle(input);
    span.style.font = styles.font;
    span.style.letterSpacing = styles.letterSpacing;
    span.textContent = val || ph || ' ';
    const containerWidth = input.parentElement?.clientWidth ?? Number.POSITIVE_INFINITY;
    const contentWidth = Math.max(span.offsetWidth, 20);
    input.style.width = `${Math.min(contentWidth, containerWidth)}px`;
  };

  useEffect(() => {
    return () => {
      if (measureSpan.current) {
        document.body.removeChild(measureSpan.current);
        measureSpan.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const prev = prevChunksRef.current;
    prevChunksRef.current = chunks;

    const newField = chunks.find((c) => c.type === 'field' && !prev.some((p) => p.id === c.id));
    if (newField) {
      const idx = chunks.findIndex((c) => c.id === newField.id);
      const next = chunks[idx + 1];
      if (next?.type === 'text') {
        const el = inputRefs.current.get(next.id);
        if (el)
          setTimeout(() => {
            el.focus();
            el.setSelectionRange(0, 0);
          }, 0);
      }
    }

    const removedField = prev.find((c) => c.type === 'field' && !chunks.some((p) => p.id === c.id));
    if (removedField) {
      const prevIdx = prev.findIndex((c) => c.id === removedField.id);
      const before = prev[prevIdx - 1];
      if (before?.type === 'text') {
        const el = inputRefs.current.get(before.id);
        if (el)
          setTimeout(() => {
            const n = el.value.length;
            el.focus();
            el.setSelectionRange(n, n);
          }, 0);
      }
    }
  }, [chunks]);

  const visible = chunks.filter((chunk, index) => {
    if (index === 0 && chunk.type === 'text' && chunk.value === '' && chunks[1]?.type === 'field') {
      return false;
    }
    return true;
  });

  let lastTextIdx = -1;
  for (let i = visible.length - 1; i >= 0; i--) {
    if (visible[i].type === 'text') {
      lastTextIdx = i;
      break;
    }
  }

  return (
    <div className="space-y-2">
      <div className="bg-white border border-input rounded-lg px-4 py-1">
        {variantLabel && <p className="text-xs text-tertiary mb-0.5">{variantLabel}</p>}
        <div className="flex flex-wrap items-center gap-2">
          {visible.map((chunk, index) =>
            chunk.type === 'field' ? (
              <div
                key={chunk.id}
                className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-card rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm text-secondary">{chunk.label ?? chunk.value}</span>
                <button
                  type="button"
                  onClick={() => onFieldRemove(chunk.id)}
                  className="rounded-full transition-colors flex items-center justify-center cursor-pointer"
                  aria-label={t('variableEditor.remove', { name: chunk.label ?? chunk.value })}
                >
                  <X className="h-3.5 w-3.5 text-secondary" />
                </button>
              </div>
            ) : (
              <input
                key={chunk.id}
                ref={(el) => {
                  if (el) {
                    inputRefs.current.set(chunk.id, el);
                    setTimeout(
                      () => adjustWidth(el, chunk.value, index === 0 ? placeholder : undefined),
                      0,
                    );
                  } else {
                    inputRefs.current.delete(chunk.id);
                  }
                }}
                type="text"
                value={chunk.value}
                placeholder={index === 0 && chunk.value === '' ? placeholder : ''}
                onChange={(e) => {
                  onTextChange(chunk.id, e.target.value);
                  adjustWidth(e.target, e.target.value, index === 0 ? placeholder : undefined);
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Backspace' &&
                    chunk.value === '' &&
                    e.currentTarget.selectionStart === 0
                  ) {
                    e.preventDefault();
                    onBackspaceOnEmpty(chunk.id);
                  }
                }}
                className={`py-1 text-base border-none focus:outline-none focus:ring-0 placeholder:text-tertiary max-w-full ${
                  index === lastTextIdx ? 'grow' : 'inline-block'
                }`}
                style={{ minWidth: '20px' }}
              />
            ),
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        {error ? <p className="text-sm text-red-500">{error}</p> : <span />}
        {maxLength !== undefined && (
          <p
            className={`text-xs text-right ${currentLength > maxLength ? 'text-red-500' : 'text-tertiary'}`}
          >
            {currentLength}/{maxLength}
          </p>
        )}
      </div>

      {hint && (
        <p className="flex items-center gap-1 text-xs text-secondary">
          <CircleAlert size={16} /> {hint}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {variables.map((v) => (
          <button
            key={v.value}
            type="button"
            onClick={() => onAddVariable(v)}
            className="px-3 py-1 rounded-md bg-decorative-1 cursor-pointer hover:bg-decorative-1/80 transition-colors"
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}
